import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Legend,
} from 'recharts';
import { format, subMonths } from 'date-fns';
import { Activity, AlertCircle, RefreshCw } from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';

const DEFAULT_LAG_WEEKS = 10;

export default function M2BitcoinLagTracker() {
  const market = useMarketData();
  const { btc, fred, equityHistory } = market;

  const [lagWeeks, setLagWeeks] = useState(DEFAULT_LAG_WEEKS);
  const [lookbackMonths, setLookbackMonths] = useState(36);

  const m2Series = fred.m2 || [];
  const btcSeries = btc.history || [];
  const dxySeries = equityHistory.dxy || [];

  const coreLoading = fred.status === 'loading' || btc.status === 'loading';
  const noData = !m2Series.length || !btcSeries.length;

  const analysis = useMemo(() => {
    if (!m2Series.length || !btcSeries.length) return null;

    const cutoff = subMonths(new Date(), lookbackMonths);
    const m2Filtered = m2Series.filter(p => p.date >= cutoff);
    const btcFiltered = btcSeries.filter(p => p.date >= cutoff);

    // M2 YoY series
    const m2YoYSeries = computeYoY(m2Filtered);
    // BTC YoY series
    const btcYoYSeries = computeYoY(btcFiltered);
    // DXY YoY series (for context)
    const dxyFiltered = dxySeries.filter(p => p.date >= cutoff);
    const dxyYoYSeries = computeYoY(dxyFiltered);

    // Build chart data with M2 shifted forward by lagWeeks
    const lagMonths = Math.round(lagWeeks / 4.33);
    const btcMap = mapByMonth(btcYoYSeries);
    const dxyMap = mapByMonth(dxyYoYSeries);

    const chartData = m2YoYSeries.map(p => {
      const shiftedDate = new Date(p.date.getFullYear(), p.date.getMonth() + lagMonths, 1);
      const btcKey = monthKey(shiftedDate);
      const dxyKey = monthKey(p.date);
      return {
        date: format(shiftedDate, 'MMM yy'),
        ts: shiftedDate.getTime(),
        m2YoY: p.value,
        btcYoY: btcMap.get(btcKey) ?? null,
        dxyYoY: dxyMap.get(dxyKey) ?? null,
      };
    }).filter(d => d.btcYoY != null || d.m2YoY != null);

    // Compute rolling correlation (12-month window)
    const rollingCorr = computeRollingCorrelation(m2YoYSeries, btcYoYSeries, lagMonths, 12);

    // Current divergence: latest M2 YoY vs latest BTC YoY
    const latestM2YoY = m2YoYSeries.length ? m2YoYSeries[m2YoYSeries.length - 1].value : null;
    const latestBtcYoY = btcYoYSeries.length ? btcYoYSeries[btcYoYSeries.length - 1].value : null;
    const latestDxyYoY = dxyYoYSeries.length ? dxyYoYSeries[dxyYoYSeries.length - 1].value : null;

    // Is BTC diverging from M2?
    const isDiverging = latestM2YoY != null && latestBtcYoY != null &&
      latestM2YoY > 3 && latestBtcYoY < 0;

    // Lag-adjusted correlation (current)
    const currentCorr = rollingCorr.length ? rollingCorr[rollingCorr.length - 1].value : null;

    return {
      chartData,
      rollingCorr,
      latestM2YoY,
      latestBtcYoY,
      latestDxyYoY,
      isDiverging,
      currentCorr,
    };
  }, [m2Series, btcSeries, dxySeries, lagWeeks, lookbackMonths]);

  if (coreLoading && noData) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-20 rounded-xl bg-slate-900 border border-slate-800" />
        <div className="h-72 rounded-2xl bg-slate-900 border border-slate-800" />
      </div>
    );
  }

  if (noData || !analysis) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-300">M2-Bitcoin lag data unavailable</h3>
            <button onClick={() => { market.refetch.fred(); market.refetch.btc(); }}
                    className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded bg-red-500/20 border border-red-500/40 text-red-300">
              <RefreshCw className="w-3 h-3" /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { chartData, latestM2YoY, latestBtcYoY, latestDxyYoY, isDiverging, currentCorr } = analysis;

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-orange-500/[0.03] via-slate-900/60 to-violet-500/[0.03] backdrop-blur-xl p-4 md:p-6">
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="relative space-y-5">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-400" />
              M2 → Bitcoin Lag Tracker
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Global M2 YoY shifted forward {lagWeeks} weeks — BTC historically follows with ~94% correlation
            </p>
          </div>
          <div className="inline-flex bg-slate-900/60 border border-white/10 rounded-lg p-0.5">
            {[{ label: '2Y', m: 24 }, { label: '3Y', m: 36 }, { label: '5Y', m: 60 }, { label: 'Max', m: 200 }].map(opt => (
              <button key={opt.label} onClick={() => setLookbackMonths(opt.m)}
                      className={`px-2.5 py-1 text-xs font-mono rounded-md transition-colors ${
                        lookbackMonths === opt.m ? 'bg-orange-500 text-black font-bold' : 'text-slate-300 hover:bg-white/5'
                      }`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Live Signal Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <MetricCard
            label="M2 YoY"
            value={latestM2YoY != null ? `${latestM2YoY >= 0 ? '+' : ''}${latestM2YoY.toFixed(1)}%` : '—'}
            tone={latestM2YoY == null ? 'neutral' : latestM2YoY > 5 ? 'bullish' : latestM2YoY > 0 ? 'neutral' : 'bearish'}
            sub={latestM2YoY != null ? (latestM2YoY > 5 ? 'Expansion — bullish for BTC in ~10wk' : latestM2YoY > 0 ? 'Modest growth' : 'Contraction — bearish signal') : null}
          />
          <MetricCard
            label="BTC YoY"
            value={latestBtcYoY != null ? `${latestBtcYoY >= 0 ? '+' : ''}${latestBtcYoY.toFixed(0)}%` : '—'}
            tone={latestBtcYoY == null ? 'neutral' : latestBtcYoY > 20 ? 'bullish' : latestBtcYoY > 0 ? 'neutral' : 'bearish'}
          />
          <MetricCard
            label="DXY YoY"
            value={latestDxyYoY != null ? `${latestDxyYoY >= 0 ? '+' : ''}${latestDxyYoY.toFixed(1)}%` : '—'}
            tone={latestDxyYoY == null ? 'neutral' : latestDxyYoY > 3 ? 'bearish' : latestDxyYoY < -3 ? 'bullish' : 'neutral'}
            sub={latestDxyYoY != null ? (latestDxyYoY > 3 ? 'Strong $ — M2 signal muted' : latestDxyYoY < -3 ? 'Weak $ — M2 flows into BTC' : 'Dollar neutral') : null}
          />
          <MetricCard
            label="Lag Correlation"
            value={currentCorr != null ? currentCorr.toFixed(2) : '—'}
            tone={currentCorr == null ? 'neutral' : currentCorr > 0.7 ? 'bullish' : currentCorr > 0.3 ? 'neutral' : 'bearish'}
            sub={currentCorr != null ? (currentCorr > 0.7 ? 'Relationship intact' : currentCorr > 0.3 ? 'Moderate linkage' : 'Decoupled — other drivers dominate') : null}
          />
        </div>

        {/* Divergence Alert */}
        {isDiverging && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-300 leading-relaxed">
              <strong>Divergence detected:</strong> M2 is expanding (+{latestM2YoY?.toFixed(1)}% YoY) but BTC is negative ({latestBtcYoY?.toFixed(0)}% YoY).
              {latestDxyYoY != null && latestDxyYoY > 3 && ' Dollar strength (+' + latestDxyYoY.toFixed(1) + '%) is likely absorbing the liquidity signal.'}
              {' '}Historically this resolves within 2-4 months as the lag catches up, unless structural demand shifts persist.
            </div>
          </div>
        )}

        {/* Lag slider */}
        <div className="bg-slate-900/40 border border-white/10 rounded-lg px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">M2 Lead Time</span>
            <span className="font-mono text-sm font-bold text-orange-400 tabular-nums">{lagWeeks} weeks</span>
          </div>
          <input
            type="range" min="0" max="24" step="1" value={lagWeeks}
            onChange={(e) => setLagWeeks(parseInt(e.target.value))}
            className="w-full accent-orange-400"
          />
          <div className="flex justify-between text-[10px] text-slate-600 mt-1 font-mono">
            <span>0w (no lag)</span>
            <span>10w (typical)</span>
            <span>24w (max)</span>
          </div>
        </div>

        {/* Chart: M2 YoY (shifted) vs BTC YoY */}
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false} />
              <YAxis
                yAxisId="m2"
                tick={{ fill: 'rgba(167,139,250,0.9)', fontSize: 11 }}
                tickLine={false}
                tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
              />
              <YAxis
                yAxisId="btc"
                orientation="right"
                tick={{ fill: 'rgba(247,147,26,0.9)', fontSize: 11 }}
                tickLine={false}
                tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
              />
              <ReferenceLine yAxisId="m2" y={0} stroke="rgba(255,255,255,0.15)" />

              <Line yAxisId="m2" type="monotone" dataKey="m2YoY" stroke="#a78bfa" strokeWidth={2.5} dot={false} name={`M2 YoY (shifted +${lagWeeks}wk)`} />
              <Line yAxisId="btc" type="monotone" dataKey="btcYoY" stroke="#f7931a" strokeWidth={2} dot={false} name="BTC YoY" />
              {dxySeries.length > 0 && (
                <Line yAxisId="m2" type="monotone" dataKey="dxyYoY" stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 3" dot={false} name="DXY YoY (inverted signal)" />
              )}

              <Tooltip
                cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
                content={<LagTooltip lagWeeks={lagWeeks} />}
              />
              <Legend wrapperStyle={{ paddingTop: 8, fontSize: 11 }} iconType="line"
                      formatter={(v) => <span className="text-slate-300">{v}</span>} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Transmission Status — live data replacing static context cards */}
        <TransmissionStatus
          m2Series={m2Series}
          btcSeries={btcSeries}
          dxySeries={dxySeries}
          ffrSeries={fred.ffr || []}
          latestM2YoY={latestM2YoY}
          latestBtcYoY={latestBtcYoY}
          latestDxyYoY={latestDxyYoY}
        />
      </div>
    </section>
  );
}

function TransmissionStatus({ m2Series, btcSeries, dxySeries, ffrSeries, latestM2YoY, latestBtcYoY, latestDxyYoY }) {
  const status = useMemo(() => {
    // DXY: 3-month direction
    const dxy3m = (() => {
      if (dxySeries.length < 4) return null;
      const latest = dxySeries[dxySeries.length - 1]?.value;
      const prior = dxySeries[dxySeries.length - 4]?.value;
      if (!latest || !prior) return null;
      return ((latest / prior - 1) * 100);
    })();

    // DXY: 6-month direction (trend)
    const dxy6m = (() => {
      if (dxySeries.length < 7) return null;
      const latest = dxySeries[dxySeries.length - 1]?.value;
      const prior = dxySeries[dxySeries.length - 7]?.value;
      if (!latest || !prior) return null;
      return ((latest / prior - 1) * 100);
    })();

    // FFR: direction (is the Fed cutting?)
    const ffrDirection = (() => {
      if (ffrSeries.length < 4) return null;
      const latest = ffrSeries[ffrSeries.length - 1]?.value;
      const prior3 = ffrSeries[ffrSeries.length - 4]?.value;
      if (latest == null || prior3 == null) return null;
      const diff = latest - prior3;
      if (diff < -0.25) return 'cutting';
      if (diff > 0.25) return 'hiking';
      return 'holding';
    })();

    const latestFfr = ffrSeries.length ? ffrSeries[ffrSeries.length - 1]?.value : null;

    // M2: 3-month momentum
    const m2_3m = (() => {
      if (m2Series.length < 4) return null;
      const latest = m2Series[m2Series.length - 1]?.value;
      const prior = m2Series[m2Series.length - 4]?.value;
      if (!latest || !prior) return null;
      return ((latest / prior - 1) * 100);
    })();

    // Transmission score: 0-100 measuring how likely M2→BTC is working
    // Bullish factors: M2 rising, DXY falling, Fed cutting
    // Bearish factors: DXY rising, M2 flat/falling, Fed hiking
    let score = 50;
    if (latestM2YoY != null) {
      if (latestM2YoY > 8) score += 20;
      else if (latestM2YoY > 4) score += 10;
      else if (latestM2YoY < 0) score -= 20;
    }
    if (latestDxyYoY != null) {
      if (latestDxyYoY < -5) score += 20;
      else if (latestDxyYoY < -2) score += 10;
      else if (latestDxyYoY > 5) score -= 20;
      else if (latestDxyYoY > 2) score -= 10;
    }
    if (ffrDirection === 'cutting') score += 15;
    else if (ffrDirection === 'hiking') score -= 15;
    if (m2_3m != null) {
      if (m2_3m > 2) score += 5;
      else if (m2_3m < 0) score -= 10;
    }
    score = Math.max(0, Math.min(100, score));

    const regime = score >= 70 ? 'transmitting' : score >= 45 ? 'partial' : 'blocked';

    return { dxy3m, dxy6m, ffrDirection, latestFfr, m2_3m, score, regime };
  }, [m2Series, dxySeries, ffrSeries, latestM2YoY, latestDxyYoY]);

  if (!status) return null;

  const { dxy3m, dxy6m, ffrDirection, latestFfr, m2_3m, score, regime } = status;

  const regimeConfig = {
    transmitting: { label: 'Transmitting', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', desc: 'Conditions favor M2→BTC flow' },
    partial: { label: 'Partially Blocked', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', desc: 'Mixed signals — lag may extend' },
    blocked: { label: 'Blocked', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', desc: 'Dollar strength or risk-off overriding M2 signal' },
  };
  const rc = regimeConfig[regime];

  return (
    <div className="space-y-3">
      {/* Transmission Gauge */}
      <div className={`rounded-xl border ${rc.border} ${rc.bg} p-4`}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Transmission Status</div>
            <div className={`text-base font-bold ${rc.color}`}>{rc.label}</div>
            <p className="text-[11px] text-slate-400 mt-0.5">{rc.desc}</p>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-bold font-mono tabular-nums ${rc.color}`}>{score}</div>
            <div className="text-[10px] text-slate-500">/100</div>
          </div>
        </div>
        <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${score}%`,
              background: score >= 70 ? '#34d399' : score >= 45 ? '#fbbf24' : '#f87171',
            }}
          />
        </div>
      </div>

      {/* Factor Breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <FactorCard
          label="Dollar (DXY)"
          value={latestDxyYoY != null ? `${latestDxyYoY >= 0 ? '+' : ''}${latestDxyYoY.toFixed(1)}% YoY` : '—'}
          direction={dxy3m != null ? (dxy3m < -1 ? 'weakening' : dxy3m > 1 ? 'strengthening' : 'flat') : null}
          signal={latestDxyYoY != null ? (latestDxyYoY < -2 ? 'bullish' : latestDxyYoY > 3 ? 'bearish' : 'neutral') : 'neutral'}
          detail={dxy6m != null ? `6mo: ${dxy6m >= 0 ? '+' : ''}${dxy6m.toFixed(1)}%` : null}
        />
        <FactorCard
          label="Fed Funds"
          value={latestFfr != null ? `${latestFfr.toFixed(2)}%` : '—'}
          direction={ffrDirection}
          signal={ffrDirection === 'cutting' ? 'bullish' : ffrDirection === 'hiking' ? 'bearish' : 'neutral'}
          detail={ffrDirection === 'cutting' ? 'Easing → liquidity up' : ffrDirection === 'hiking' ? 'Tightening → liquidity down' : 'On hold'}
        />
        <FactorCard
          label="M2 Momentum"
          value={m2_3m != null ? `${m2_3m >= 0 ? '+' : ''}${m2_3m.toFixed(2)}% (3mo)` : '—'}
          direction={m2_3m != null ? (m2_3m > 1 ? 'accelerating' : m2_3m > 0 ? 'flat' : 'decelerating') : null}
          signal={m2_3m != null ? (m2_3m > 1 ? 'bullish' : m2_3m < 0 ? 'bearish' : 'neutral') : 'neutral'}
          detail={latestM2YoY != null ? `YoY: +${latestM2YoY.toFixed(1)}%` : null}
        />
        <FactorCard
          label="BTC Response"
          value={latestBtcYoY != null ? `${latestBtcYoY >= 0 ? '+' : ''}${latestBtcYoY.toFixed(0)}% YoY` : '—'}
          direction={latestBtcYoY != null ? (latestBtcYoY > 20 ? 'surging' : latestBtcYoY > 0 ? 'rising' : 'declining') : null}
          signal={latestBtcYoY != null ? (latestBtcYoY > 20 ? 'bullish' : latestBtcYoY < 0 ? 'bearish' : 'neutral') : 'neutral'}
          detail={latestM2YoY != null && latestBtcYoY != null ? (
            latestM2YoY > 3 && latestBtcYoY < 0 ? 'Diverging from M2' : latestBtcYoY > latestM2YoY ? 'Outpacing M2' : 'Tracking M2'
          ) : null}
        />
      </div>

      {/* Reassertion Conditions Checklist */}
      <ReassertionChecklist
        dxyYoY={latestDxyYoY}
        dxy3m={dxy3m}
        m2YoY={latestM2YoY}
        m2_3m={m2_3m}
        ffrDirection={ffrDirection}
      />
    </div>
  );
}

function FactorCard({ label, value, direction, signal, detail }) {
  const signalColors = { bullish: 'text-emerald-400', bearish: 'text-red-400', neutral: 'text-slate-300' };
  const dirIcons = {
    weakening: '↘', strengthening: '↗', flat: '→',
    cutting: '↘', hiking: '↗', holding: '→',
    accelerating: '↗', decelerating: '↘',
    surging: '⬆', rising: '↗', declining: '↘',
  };

  return (
    <div className="px-3 py-2.5 bg-slate-900/40 border border-white/10 rounded-lg">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`font-mono font-bold tabular-nums text-sm ${signalColors[signal]}`}>
        {direction && <span className="mr-1">{dirIcons[direction] || ''}</span>}
        {value}
      </div>
      {detail && <p className="text-[10px] text-slate-500 mt-1">{detail}</p>}
    </div>
  );
}

function ReassertionChecklist({ dxyYoY, dxy3m, m2YoY, m2_3m, ffrDirection }) {
  const checks = [
    {
      label: 'DXY turning negative YoY',
      met: dxyYoY != null && dxyYoY < 0,
      value: dxyYoY != null ? `${dxyYoY >= 0 ? '+' : ''}${dxyYoY.toFixed(1)}%` : '—',
      need: '< 0%',
    },
    {
      label: 'DXY 3-month trend weakening',
      met: dxy3m != null && dxy3m < -1,
      value: dxy3m != null ? `${dxy3m >= 0 ? '+' : ''}${dxy3m.toFixed(1)}%` : '—',
      need: '< -1%',
    },
    {
      label: 'M2 expanding (YoY > 4%)',
      met: m2YoY != null && m2YoY > 4,
      value: m2YoY != null ? `+${m2YoY.toFixed(1)}%` : '—',
      need: '> 4%',
    },
    {
      label: 'M2 3-month momentum positive',
      met: m2_3m != null && m2_3m > 0.5,
      value: m2_3m != null ? `${m2_3m >= 0 ? '+' : ''}${m2_3m.toFixed(2)}%` : '—',
      need: '> 0.5%',
    },
    {
      label: 'Fed cutting or holding',
      met: ffrDirection === 'cutting' || ffrDirection === 'holding',
      value: ffrDirection || '—',
      need: 'not hiking',
    },
  ];

  const metCount = checks.filter(c => c.met).length;

  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">
          Reassertion Conditions
        </div>
        <div className={`text-xs font-bold font-mono ${
          metCount >= 4 ? 'text-emerald-400' : metCount >= 3 ? 'text-amber-400' : 'text-red-400'
        }`}>
          {metCount}/{checks.length} met
        </div>
      </div>
      <div className="space-y-2">
        {checks.map((c, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className={`w-5 h-5 rounded flex items-center justify-center text-xs font-bold flex-shrink-0 ${
              c.met ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-600'
            }`}>
              {c.met ? '✓' : '○'}
            </span>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-slate-300">{c.label}</span>
            </div>
            <div className="text-right flex-shrink-0">
              <span className={`text-xs font-mono tabular-nums ${c.met ? 'text-emerald-400' : 'text-slate-500'}`}>
                {c.value}
              </span>
              <span className="text-[10px] text-slate-600 ml-1.5">(need {c.need})</span>
            </div>
          </div>
        ))}
      </div>
      {metCount >= 4 && (
        <p className="mt-3 text-[11px] text-emerald-400/80 border-t border-white/5 pt-2">
          Most conditions met — M2→BTC lag historically reasserts within 2-3 months in this configuration.
        </p>
      )}
      {metCount < 3 && (
        <p className="mt-3 text-[11px] text-slate-500 border-t border-white/5 pt-2">
          Transmission likely blocked. Watch for dollar reversal as the primary catalyst.
        </p>
      )}
    </div>
  );
}

function LagTooltip({ active, payload, lagWeeks }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-slate-950/95 backdrop-blur-md px-4 py-3 shadow-xl text-xs font-mono tabular-nums">
      <div className="text-slate-200 font-semibold mb-2 font-sans">{row.date}</div>
      <div className="space-y-1">
        {row.m2YoY != null && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-violet-300">M2 YoY (+{lagWeeks}wk):</span>
            <span className="text-slate-100">{row.m2YoY >= 0 ? '+' : ''}{row.m2YoY.toFixed(1)}%</span>
          </div>
        )}
        {row.btcYoY != null && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-orange-300">BTC YoY:</span>
            <span className="text-slate-100">{row.btcYoY >= 0 ? '+' : ''}{row.btcYoY.toFixed(0)}%</span>
          </div>
        )}
        {row.dxyYoY != null && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-slate-400">DXY YoY:</span>
            <span className="text-slate-100">{row.dxyYoY >= 0 ? '+' : ''}{row.dxyYoY.toFixed(1)}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, tone, sub }) {
  const tones = {
    bullish: 'text-emerald-400',
    bearish: 'text-red-400',
    neutral: 'text-slate-300',
  };
  return (
    <div className="px-3 py-2.5 bg-slate-900/40 border border-white/10 rounded-lg">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
      <div className={`font-mono font-bold tabular-nums text-lg ${tones[tone] || tones.neutral}`}>{value}</div>
      {sub && <p className="text-[10px] text-slate-500 mt-1 leading-snug">{sub}</p>}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function monthKey(d) { return `${d.getFullYear()}-${d.getMonth()}`; }

function mapByMonth(series) {
  const m = new Map();
  for (const p of series || []) {
    if (p?.date && Number.isFinite(p.value)) m.set(monthKey(p.date), p.value);
  }
  return m;
}

function computeYoY(series) {
  if (!series || series.length < 13) return [];
  const byKey = new Map();
  for (const p of series) {
    if (p?.date) byKey.set(`${p.date.getFullYear()}-${p.date.getMonth()}`, p.value);
  }
  const out = [];
  for (const p of series) {
    if (!p?.date || !Number.isFinite(p.value) || p.value <= 0) continue;
    const priorKey = `${p.date.getFullYear() - 1}-${p.date.getMonth()}`;
    const prior = byKey.get(priorKey);
    if (prior && prior > 0) {
      out.push({ date: p.date, value: ((p.value / prior - 1) * 100) });
    }
  }
  return out;
}

function computeRollingCorrelation(m2YoY, btcYoY, lagMonths, windowMonths) {
  const m2Map = mapByMonth(m2YoY);
  const btcMap = mapByMonth(btcYoY);

  const allMonths = [...new Set([...m2YoY, ...btcYoY].map(p => monthKey(p.date)))]
    .sort()
    .map(k => {
      const [y, m] = k.split('-').map(Number);
      return new Date(y, m, 1);
    });

  const out = [];
  for (let i = windowMonths; i < allMonths.length; i++) {
    const windowEnd = allMonths[i];
    const pairs = [];
    for (let j = i - windowMonths; j <= i; j++) {
      const m2Key = monthKey(new Date(allMonths[j].getFullYear(), allMonths[j].getMonth() - lagMonths, 1));
      const btcKey = monthKey(allMonths[j]);
      const m2Val = m2Map.get(m2Key);
      const btcVal = btcMap.get(btcKey);
      if (m2Val != null && btcVal != null) pairs.push([m2Val, btcVal]);
    }
    if (pairs.length >= 6) {
      const r = pearson(pairs.map(p => p[0]), pairs.map(p => p[1]));
      if (r != null) out.push({ date: windowEnd, value: r });
    }
  }
  return out;
}

function pearson(a, b) {
  const n = Math.min(a.length, b.length);
  if (n < 5) return null;
  const mx = a.slice(0, n).reduce((s, v) => s + v, 0) / n;
  const my = b.slice(0, n).reduce((s, v) => s + v, 0) / n;
  let num = 0, dx = 0, dy = 0;
  for (let i = 0; i < n; i++) {
    const ax = a[i] - mx, ay = b[i] - my;
    num += ax * ay;
    dx += ax * ax;
    dy += ay * ay;
  }
  return dx && dy ? num / Math.sqrt(dx * dy) : null;
}
